import React, { useState, useEffect, useRef } from 'react';
import { Send, ArrowLeft, User, MessageCircle, RefreshCw, Search } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Inbox Component for Users
export const UserInbox = () => {
  const { token, user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [otherUser, setOtherUser] = useState(null);
  const messagesEndRef = useRef(null);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data.items || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/conversations/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages || []);
      setOtherUser(response.data.other_user);
      // Update unread count in local state
      setConversations(prev => prev.map(c => 
        c.conversation_id === conversationId ? { ...c, my_unread: 0 } : c
      ));
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const startNewConversation = async () => {
    try {
      const response = await axios.post(`${API}/conversations`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedConversation(response.data.conversation_id);
      fetchMessages(response.data.conversation_id);
      fetchConversations();
    } catch (error) {
      toast.error('Error iniciando conversación');
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      await axios.post(`${API}/conversations/${selectedConversation}/messages`, {
        content: newMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewMessage('');
      fetchMessages(selectedConversation);
      fetchConversations();
    } catch (error) {
      toast.error('Error enviando mensaje');
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    fetchConversations();
    // Poll for new messages every 10 seconds
    const interval = setInterval(fetchConversations, 10000);
    return () => clearInterval(interval);
  }, [token]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation);
      // Poll for new messages in active conversation
      const interval = setInterval(() => fetchMessages(selectedConversation), 5000);
      return () => clearInterval(interval);
    }
  }, [selectedConversation]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (loading) {
    return (
      <div className="glass-dark rounded-xl p-8 text-center">
        <RefreshCw size={24} className="text-white/40 mx-auto animate-spin mb-2" />
        <p className="text-white/50">Cargando mensajes...</p>
      </div>
    );
  }

  // Conversation View
  if (selectedConversation) {
    return (
      <div className="glass-dark rounded-xl overflow-hidden h-[600px] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-white/10 flex items-center gap-4">
          <button 
            onClick={() => setSelectedConversation(null)}
            className="p-2 rounded-lg hover:bg-white/10 text-white/60"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-3 flex-1">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
              <User size={18} className="text-[#D4AF37]" />
            </div>
            <div>
              <p className="text-white font-medium">{otherUser?.name || 'Soporte'}</p>
              <p className="text-white/40 text-sm">{otherUser?.role === 'super_admin' ? 'Administrador' : otherUser?.role}</p>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 ? (
            <div className="text-center py-12">
              <MessageCircle size={32} className="text-white/20 mx-auto mb-3" />
              <p className="text-white/50">Inicia la conversación</p>
            </div>
          ) : (
            messages.map((msg) => {
              const isMe = msg.sender_id === user?.user_id;
              return (
                <div 
                  key={msg.message_id}
                  className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[70%] rounded-2xl px-4 py-3 ${
                    isMe 
                      ? 'bg-[#D4AF37] text-black' 
                      : 'bg-white/10 text-white'
                  }`}>
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    <p className={`text-xs mt-1 ${isMe ? 'text-black/50' : 'text-white/40'}`}>
                      {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-white/10">
          <div className="flex gap-3">
            <Textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Escribe tu mensaje..."
              className="input-premium flex-1 resize-none"
              rows={2}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
            />
            <button
              onClick={sendMessage}
              disabled={sending || !newMessage.trim()}
              className="btn-gold px-4 self-end disabled:opacity-50"
            >
              {sending ? <RefreshCw size={18} className="animate-spin" /> : <Send size={18} />}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Conversations List
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl text-white mb-1">Mensajes</h2>
          <p className="text-white/50 text-sm">Comunícate con el equipo de GS Agency</p>
        </div>
        <button onClick={startNewConversation} className="btn-gold flex items-center gap-2">
          <MessageCircle size={18} /> Nueva Conversación
        </button>
      </div>

      {conversations.length === 0 ? (
        <div className="glass-dark rounded-xl p-12 text-center">
          <MessageCircle size={48} className="text-white/20 mx-auto mb-4" />
          <h3 className="text-white font-display text-xl mb-2">Sin conversaciones</h3>
          <p className="text-white/50 mb-6">Inicia una conversación con el equipo de soporte</p>
          <button onClick={startNewConversation} className="btn-gold">
            Iniciar Conversación
          </button>
        </div>
      ) : (
        <div className="glass-dark rounded-xl overflow-hidden divide-y divide-white/5">
          {conversations.map((conv) => (
            <button
              key={conv.conversation_id}
              onClick={() => setSelectedConversation(conv.conversation_id)}
              className="w-full p-4 hover:bg-white/5 transition-colors text-left flex items-center gap-4"
            >
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                {conv.other_user?.picture ? (
                  <img src={conv.other_user.picture} alt="" className="w-full h-full rounded-full object-cover" />
                ) : (
                  <User size={20} className="text-white/40" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-white font-medium truncate">{conv.other_user?.name || 'Soporte'}</p>
                  {conv.my_unread > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-[#D4AF37] text-black text-xs font-bold">
                      {conv.my_unread}
                    </span>
                  )}
                </div>
                <p className="text-white/50 text-sm truncate">
                  {conv.last_message?.content || 'Sin mensajes'}
                </p>
                <p className="text-white/30 text-xs mt-1">
                  {conv.updated_at ? new Date(conv.updated_at).toLocaleDateString() : ''}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Admin Inbox Component
export const AdminInbox = ({ token }) => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [otherUser, setOtherUser] = useState(null);
  const [search, setSearch] = useState('');
  const messagesEndRef = useRef(null);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/admin/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data.items || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/conversations/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages || []);
      setOtherUser(response.data.other_user);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      await axios.post(`${API}/conversations/${selectedConversation}/messages`, {
        content: newMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewMessage('');
      fetchMessages(selectedConversation);
      fetchConversations();
    } catch (error) {
      toast.error('Error enviando mensaje');
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    fetchConversations();
    const interval = setInterval(fetchConversations, 10000);
    return () => clearInterval(interval);
  }, [token]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation);
      const interval = setInterval(() => fetchMessages(selectedConversation), 5000);
      return () => clearInterval(interval);
    }
  }, [selectedConversation]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const filteredConversations = conversations.filter(conv => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return conv.participants_info?.some(p => 
      p.name?.toLowerCase().includes(searchLower) || 
      p.email?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="glass-dark rounded-xl p-8 text-center">
        <RefreshCw size={24} className="text-white/40 mx-auto animate-spin mb-2" />
        <p className="text-white/50">Cargando conversaciones...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl text-white mb-2">Inbox</h1>
        <p className="text-white/50">Conversaciones con usuarios de la plataforma</p>
      </div>

      <div className="grid grid-cols-3 gap-6 h-[600px]">
        {/* Conversations List */}
        <div className="glass-dark rounded-xl overflow-hidden flex flex-col">
          <div className="p-4 border-b border-white/10">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" size={18} />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar conversación..."
                className="input-premium pl-10 text-sm"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-white/5">
            {filteredConversations.length === 0 ? (
              <div className="p-8 text-center">
                <MessageCircle size={32} className="text-white/20 mx-auto mb-3" />
                <p className="text-white/50 text-sm">No hay conversaciones</p>
              </div>
            ) : (
              filteredConversations.map((conv) => {
                const otherParticipant = conv.participants_info?.find(p => p.role !== 'super_admin' && p.role !== 'admin');
                const isSelected = selectedConversation === conv.conversation_id;
                
                return (
                  <button
                    key={conv.conversation_id}
                    onClick={() => setSelectedConversation(conv.conversation_id)}
                    className={`w-full p-4 text-left transition-colors ${
                      isSelected ? 'bg-[#D4AF37]/10 border-l-2 border-[#D4AF37]' : 'hover:bg-white/5'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                        <User size={18} className="text-white/40" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-white font-medium text-sm truncate">
                            {otherParticipant?.name || 'Usuario'}
                          </p>
                          {conv.admin_unread > 0 && (
                            <span className="w-2 h-2 rounded-full bg-[#D4AF37]" />
                          )}
                        </div>
                        <p className="text-white/40 text-xs truncate">
                          {otherParticipant?.email || ''}
                        </p>
                        <p className="text-white/30 text-xs mt-1 truncate">
                          {conv.last_message?.content || 'Sin mensajes'}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className="col-span-2 glass-dark rounded-xl overflow-hidden flex flex-col">
          {selectedConversation ? (
            <>
              {/* Header */}
              <div className="p-4 border-b border-white/10 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                  <User size={18} className="text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-white font-medium">{otherUser?.name || 'Usuario'}</p>
                  <p className="text-white/40 text-sm">{otherUser?.email}</p>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((msg) => {
                  const isMe = msg.sender_id === user?.user_id;
                  return (
                    <div 
                      key={msg.message_id}
                      className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[70%] rounded-2xl px-4 py-3 ${
                        isMe 
                          ? 'bg-[#D4AF37] text-black' 
                          : 'bg-white/10 text-white'
                      }`}>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        <p className={`text-xs mt-1 ${isMe ? 'text-black/50' : 'text-white/40'}`}>
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="p-4 border-t border-white/10">
                <div className="flex gap-3">
                  <Textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Escribe tu respuesta..."
                    className="input-premium flex-1 resize-none"
                    rows={2}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                  />
                  <button
                    onClick={sendMessage}
                    disabled={sending || !newMessage.trim()}
                    className="btn-gold px-4 self-end disabled:opacity-50"
                  >
                    {sending ? <RefreshCw size={18} className="animate-spin" /> : <Send size={18} />}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageCircle size={48} className="text-white/20 mx-auto mb-4" />
                <p className="text-white/50">Selecciona una conversación</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserInbox;
